import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isTrialUser, getUserTrialEndDate } from '../utils/subscriptionPlan';
import TrialExpiredModal from './TrialExpiredModal';

function ClockIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 2.5a7.5 7.5 0 100 15 7.5 7.5 0 000-15zM4 10a6 6 0 1112 0 6 6 0 01-12 0zm6.75-3.25a.75.75 0 00-1.5 0V10c0 .2.08.39.22.53l2.5 2.5a.75.75 0 101.06-1.06L10.75 9.7V6.75z"
        fill="currentColor"
      />
    </svg>
  );
}

const T = {
  fr: {
    title: (days) => {
      if (days == null) return 'Essai gratuit en cours';
      if (days <= 0) return 'Votre essai gratuit se termine aujourd’hui !';
      if (days === 1) return 'Il ne vous reste qu’1 jour d’essai gratuit !';
      if (days <= 3) return `Il ne vous reste que ${days} jours d’essai gratuit !`;
      return `${days} jours restants sur votre essai gratuit`;
    },
    subtitle: (date) => (date
      ? `Accès complet à toutes les fonctionnalités jusqu'au ${date}. Passez à un forfait payant pour ne rien perdre.`
      : 'Accès complet à toutes les fonctionnalités. Passez à un forfait payant pour ne rien perdre.'),
    cta: 'Choisir un forfait',
    dayUnit: 'j',
  },
  en: {
    title: (days) => {
      if (days == null) return 'Free trial in progress';
      if (days <= 0) return 'Your free trial ends today!';
      if (days === 1) return 'Only 1 day left on your free trial!';
      if (days <= 3) return `Only ${days} days left on your free trial!`;
      return `${days} days left on your free trial`;
    },
    subtitle: (date) => (date
      ? `Full access to every feature until ${date}. Upgrade to a paid plan to keep it all.`
      : 'Full access to every feature. Upgrade to a paid plan to keep it all.'),
    cta: 'Choose a plan',
    dayUnit: 'd',
  },
  ar: {
    title: (days) => {
      if (days == null) return 'الفترة التجريبية المجانية جارية';
      if (days <= 0) return 'تنتهي فترتك التجريبية اليوم!';
      if (days === 1) return 'تبقى لك يوم واحد فقط من الفترة التجريبية!';
      if (days <= 3) return `تبقى لك ${days} أيام فقط من الفترة التجريبية!`;
      return `تبقى لك ${days} يومًا من الفترة التجريبية`;
    },
    subtitle: (date) => (date
      ? `وصول كامل لجميع الميزات حتى ${date}. قم بالترقية إلى باقة مدفوعة حتى لا تفقد شيئًا.`
      : 'وصول كامل لجميع الميزات. قم بالترقية إلى باقة مدفوعة حتى لا تفقد شيئًا.'),
    cta: 'اختر باقة',
    dayUnit: 'ي',
  },
};

function formatEndDate(raw, lang) {
  if (!raw) return null;
  try {
    return new Date(raw).toLocaleDateString(lang === 'ar' ? 'ar-DZ' : lang === 'en' ? 'en-GB' : 'fr-DZ', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch {
    return null;
  }
}

function pad(n) {
  return String(n).padStart(2, '0');
}

/** Live D/H/M/S left until `endRaw`, ticking every second. Null once expired or no end date. */
function useCountdown(endRaw) {
  const end = endRaw ? new Date(endRaw).getTime() : null;
  const [msLeft, setMsLeft] = useState(() => (end ? end - Date.now() : null));

  useEffect(() => {
    if (!end) { setMsLeft(null); return undefined; }
    setMsLeft(end - Date.now());
    const id = setInterval(() => setMsLeft(end - Date.now()), 1000);
    return () => clearInterval(id);
  }, [end]);

  if (msLeft == null || Number.isNaN(msLeft) || msLeft <= 0) return null;

  const totalSeconds = Math.floor(msLeft / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}

export default function TrialBanner({ user, lang = 'fr' }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  // Raw flag, not isTrialUser() — we only want to flag expiry when the clock
  // runs out on an actual trial, never when is_trial flips false because the
  // user upgraded (a fresh /connect/user would already say so on next render).
  const rawIsTrial = Boolean(user?.subscription?.is_trial);
  const endRaw = rawIsTrial ? getUserTrialEndDate(user) : null;
  const countdown = useCountdown(endRaw);
  const timeExpired = rawIsTrial && Boolean(endRaw) && !countdown;
  // One last chance to upgrade before ending the session — reset per mount so
  // a reload still nudges an unpaid, expired trial user again.
  const [expiredModalDismissed, setExpiredModalDismissed] = useState(false);

  const t = T[lang] || T.fr;
  const isRtl = lang === 'ar';

  if (timeExpired && !expiredModalDismissed) {
    return (
      <TrialExpiredModal
        lang={lang}
        onUpgrade={() => {
          setExpiredModalDismissed(true);
          navigate('/channels/kiostore?tab=plan');
        }}
        onLogout={logout}
      />
    );
  }

  if (!isTrialUser(user)) return null;

  // Same floor-based day count as the countdown chip, so the two never disagree.
  const days = countdown ? countdown.days : null;
  const dateLabel = formatEndDate(endRaw, lang);
  const urgent = days != null && days <= 3;

  return (
    <div
      dir={isRtl ? 'rtl' : 'ltr'}
      className={`kio-trial-banner ${urgent ? 'kio-trial-banner--urgent' : 'kio-trial-banner--normal'}`}
    >
      <span className="kio-trial-banner__icon" aria-hidden="true"><ClockIcon /></span>
      <div className="kio-trial-banner__body">
        {/* aria-live here (not on the seconds-ticking countdown below) — this only
            changes once a day at most, so it won't spam screen readers every tick. */}
        <div className="kio-trial-banner__title" aria-live="polite">{t.title(days)}</div>
        <div className="kio-trial-banner__subtitle">{t.subtitle(dateLabel)}</div>
      </div>
      {countdown && (
        <div className="kio-trial-banner__countdown" aria-hidden="true">
          {countdown.days > 0 && (
            <span className="kio-trial-banner__countdown-days">{countdown.days}{t.dayUnit}</span>
          )}
          <span className="kio-trial-banner__countdown-clock">
            {pad(countdown.hours)}:{pad(countdown.minutes)}:{pad(countdown.seconds)}
          </span>
        </div>
      )}
      <button
        type="button"
        className="kio-trial-banner__cta"
        onClick={() => navigate('/channels/kiostore?tab=plan')}
      >
        {t.cta}
      </button>
    </div>
  );
}
